import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { SafeLink } from "@/components/common/SafeLink";
import { useNavigationBlocker } from "@/contexts/NavigationBlockerContext";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import { useProfile } from "@/hooks/useProfile";
import { Notification } from "@/types";
import {
  getTelegramMagicLink,
  isTelegramWebApp,
  openLink,
} from "@/integrations/telegram/telegramService";
import {
  Bell,
  Menu,
  X,
  Upload,
  FileText,
  CreditCard,
  User,
  LogOut,
  LayoutTemplate,
  Scale,
  ArrowLeft,
  Clock,
  ChevronLeft,
  Mail,
  MessageCircle,
  Crown,
  Globe,
  HelpCircle,
} from "lucide-react";
import { useNotifications } from "@/contexts/NotificationContext";
import NotificationsPopover from "@/components/notifications/NotificationsPopover";
import { useTelegram } from "@/contexts/UnifiedAuthContext";
import {
  closeTelegramApp,
  logoutAndCloseTelegramApp,
} from "@/integrations/telegram/telegramService";
import { useNavigate } from "react-router-dom";
import { ThemeToggleCompact } from "@/components/theme/ThemeToggle";
import { formatMinutes } from "@/lib/utils";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { useTranslation } from "react-i18next";
// import { useWizardContext } from "@/contexts/WizardContext";

interface HeaderProps {
  onBackClick?: () => void;
  showBackButton?: boolean;
  disabled?: boolean;
}

const Header = ({ onBackClick, showBackButton, disabled = false }: HeaderProps = {}) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { balance, isLoading: isBalanceLoading } = useProfile(user?.id);
  const { unreadCount, addNotification } = useNotifications();
  const { isTelegram, telegramUser } = useTelegram();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { checkNavigation } = useNavigationBlocker();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const isHomePage = location.pathname === "/";

  const handleBackClick = async () => {
    console.log("[Header] Клик по кнопке назад");

    try {
      // Проверяем, можно ли выполнить навигацию
      const canNavigate = await checkNavigation();
      console.log("[Header] Можем навигировать назад:", canNavigate);

      if (canNavigate) {
        if (onBackClick) {
          console.log("[Header] Используем переданный обработчик onBackClick");
          // Используем переданный обработчик (для мастера)
          onBackClick();
        } else {
          console.log("[Header] Стандартная навигация назад");
          // Проверяем, есть ли история для возврата назад
          if (window.history.length > 1) {
            // Стандартная навигация назад
            navigate(-1);
          } else {
            // Если истории нет, переходим на главную страницу
            console.log("[Header] История пуста, переходим на главную страницу");
            navigate("/");
          }
        }
      }
    } catch (error) {
      console.error("[Header] Ошибка при обработке клика назад:", error);
      // В случае ошибки все равно пытаемся выполнить навигацию
      if (onBackClick) {
        onBackClick();
      } else {
        // Проверяем историю и в случае ошибки тоже
        if (window.history.length > 1) {
          navigate(-1);
        } else {
          navigate("/");
        }
      }
    }
  };

  const renderUserInfo = () => {
    if (isTelegram && telegramUser) {
      // Для Telegram отображаем username или имя пользователя
      return telegramUser.username
        ? `@${telegramUser.username}`
        : telegramUser.first_name;
    }
    // Для веб-приложения отображаем email
    return user?.email || "";
  };

  const renderMobileUserInfo = () => {
    const userInfo = renderUserInfo();
    const Icon = isTelegram ? MessageCircle : Mail;

    return (
      <div className="flex items-center justify-center gap-2 px-4 py-3 mx-4 bg-gradient-to-r from-brand-50 to-blue-50 rounded-xl border border-brand-100">
        <Icon size={16} className="text-brand-500" />
        <span className="text-sm font-medium text-brand-700">{userInfo}</span>
      </div>
    );
  };

  const renderBalance = () => {
    if (isBalanceLoading) {
      return <div className="animate-pulse h-4 w-20 bg-muted rounded"></div>;
    }
    return `${formatMinutes(balance ?? 0)} на балансе`;
  };

  return (
    <header className="sticky top-0 z-[60] w-full bg-card border-b">
      <div className="container px-4 mx-auto flex h-16 items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Кнопка назад */}
          {user && (showBackButton || !isHomePage) && (
            <Button
              variant="ghost"
              size="icon"
              className={`flex-shrink-0 hover:bg-brand-50 ${disabled ? 'opacity-50 pointer-events-none cursor-not-allowed' : ''}`}
              onClick={handleBackClick}
              disabled={disabled}
            >
              <ChevronLeft size={20} className="text-muted-foreground" />
            </Button>
          )}

          {/* Логотип с балансом */}
          <div className="flex flex-col items-center">
            <Link
              to="/"
              className={`flex items-center space-x-2 font-bold text-xl text-brand-500 ${disabled ? 'pointer-events-none cursor-not-allowed opacity-70' : ''}`}
            >
              <span>Voice2Doc</span>
            </Link>
            {user && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {isBalanceLoading ? (
                  <div className="animate-pulse h-3 w-16 bg-muted rounded"></div>
                ) : (
                  <>
                    {t('header.balance')}:{" "}
                    <span className="font-semibold text-brand-600">
                      {formatMinutes(balance ?? 0)}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {user ? (
          <>
            <nav className={`hidden md:flex items-center space-x-1 ${disabled ? 'opacity-50 pointer-events-none cursor-not-allowed' : ''}`}>
              <SafeLink
                to="/"
                className={`nav-link ${isActivePath("/") ? "nav-link-active" : ""
                  } ${disabled ? 'cursor-not-allowed' : ''}`}
              >
                <Upload size={18} />
                <span>{t('common.upload')}</span>
              </SafeLink>
              <SafeLink
                to="/dashboard"
                className={`nav-link ${isActivePath("/dashboard") ? "nav-link-active" : ""
                  } ${disabled ? 'cursor-not-allowed' : ''}`}
              >
                <FileText size={18} />
                <span>{t('common.my_records')}</span>
              </SafeLink>
              <SafeLink
                to="/templates"
                className={`nav-link ${isActivePath("/templates") ? "nav-link-active" : ""
                  } ${disabled ? 'cursor-not-allowed' : ''}`}
              >
                <LayoutTemplate size={18} />
                <span>{t('common.templates')}</span>
              </SafeLink>
              <SafeLink
                to="/balance"
                className={`nav-link ${isActivePath("/balance") ? "nav-link-active" : ""
                  } ${disabled ? 'cursor-not-allowed' : ''}`}
              >
                <CreditCard size={18} />
                <span>{t('common.balance')}</span>
              </SafeLink>
              <SafeLink
                to="/support"
                className={`nav-link ${isActivePath("/support") ? "nav-link-active" : ""
                  } ${disabled ? 'cursor-not-allowed' : ''}`}
              >
                <HelpCircle size={18} />
                <span>{t('common.support')}</span>
              </SafeLink>
            </nav>

            <div className={`flex items-center space-x-2 ${disabled ? 'opacity-50 pointer-events-none cursor-not-allowed' : ''}`}>
              <NotificationsPopover>
                <Button
                  variant="outline"
                  size="icon"
                  className={`relative ${disabled ? 'cursor-not-allowed' : ''}`}
                  disabled={disabled}
                >
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 h-4 w-4 text-[10px] font-medium flex items-center justify-center bg-red-500 text-primary-foreground rounded-full transform translate-x-1 -translate-y-1">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </NotificationsPopover>

              {/* Переключатель тем для десктопа (скрывается в Telegram) */}
              <div className={disabled ? 'pointer-events-none' : ''}>
                <ThemeToggleCompact />
              </div>

              <div className={disabled ? 'pointer-events-none' : ''}>
                <LanguageSwitcher />
              </div>

              {/* Мобильное бургер-меню скрыто, так как заменено на Bottom Navigation */}
              <Button
                variant="ghost"
                size="sm"
                className="hidden"
                onClick={toggleMobileMenu}
                disabled={disabled}
              >
                <Menu size={24} />
              </Button>

              <div className={`hidden md:flex items-center space-x-1 ${disabled ? 'pointer-events-none' : ''}`}>
                <SafeLink to="/profile">
                  <Button
                    variant="ghost"
                    size="icon"
                    className={disabled ? 'cursor-not-allowed' : ''}
                    disabled={disabled}
                  >
                    <User size={18} />
                  </Button>
                </SafeLink>
                <Button
                  variant="ghost"
                  size="icon"
                  className={disabled ? 'cursor-not-allowed' : ''}
                  disabled={disabled}
                  onClick={async () => {
                    if (!disabled) {
                      const canNavigate = await checkNavigation();
                      if (canNavigate) {
                        logout();
                      }
                    }
                  }}
                >
                  <LogOut size={18} />
                </Button>
              </div>
            </div>

            {/* Mobile Menu - скрыто на мобильных, так как заменено на Bottom Navigation */}
            {mobileMenuOpen && (
              <div className="fixed inset-0 z-50 bg-card hidden md:hidden flex flex-col">
                {/* Header */}
                <div className="container h-16 flex items-center justify-between">
                  <SafeLink
                    to="/"
                    className="flex items-center space-x-2 font-bold text-xl text-brand-500"
                  >
                    <span>Voice2Doc</span>
                  </SafeLink>
                  <Button variant="ghost" size="sm" onClick={closeMobileMenu}>
                    <X size={24} />
                  </Button>
                </div>

                {/* User Info */}
                <div className="py-4 border-b">{renderMobileUserInfo()}</div>

                {/* Main Navigation */}
                <nav className="flex-1 flex flex-col px-4 py-2">
                  <div className="space-y-1">
                    <SafeLink
                      to="/"
                      className={`nav-link text-base py-3 ${isActivePath("/") ? "nav-link-active" : ""
                        }`}
                      onClick={closeMobileMenu}
                    >
                      <Upload size={20} />
                      <span>{t('common.upload_file')}</span>
                    </SafeLink>
                    <SafeLink
                      to="/dashboard"
                      className={`nav-link text-base py-3 ${isActivePath("/dashboard") ? "nav-link-active" : ""
                        }`}
                      onClick={closeMobileMenu}
                    >
                      <FileText size={20} />
                      <span>{t('common.my_records')}</span>
                    </SafeLink>
                    <SafeLink
                      to="/templates"
                      className={`nav-link text-base py-3 ${isActivePath("/templates") ? "nav-link-active" : ""
                        }`}
                      onClick={closeMobileMenu}
                    >
                      <LayoutTemplate size={20} />
                      <span>{t('common.templates')}</span>
                    </SafeLink>
                    <SafeLink
                      to="/balance"
                      className={`nav-link text-base py-3 ${isActivePath("/balance") ? "nav-link-active" : ""
                        }`}
                      onClick={closeMobileMenu}
                    >
                      <CreditCard size={20} />
                      <span>{t('common.balance_and_payment')}</span>
                    </SafeLink>
                    <SafeLink
                      to="/profile"
                      className={`nav-link text-base py-3 ${isActivePath("/profile") ? "nav-link-active" : ""
                        }`}
                      onClick={closeMobileMenu}
                    >
                      <User size={20} />
                      <span>{t('common.profile')}</span>
                    </SafeLink>
                    <SafeLink
                      to="/support"
                      className={`nav-link text-base py-3 ${isActivePath("/support") ? "nav-link-active" : ""
                        }`}
                      onClick={closeMobileMenu}
                    >
                      <HelpCircle size={20} />
                      <span>{t('common.support')}</span>
                    </SafeLink>
                  </div>

                  {/* Bottom section - прибитые к низу элементы */}
                  <div className="mt-auto pb-4 space-y-1 border-t pt-4">
                    {isTelegramWebApp() && (
                      <Button
                        variant="ghost"
                        className={`w-full flex items-center justify-start gap-3 px-4 py-3 text-base nav-link ${isActivePath("/browser") ? "nav-link-active" : ""
                          }`}
                        onClick={async () => {
                          try {
                            const magicLink = await getTelegramMagicLink();
                            openLink(magicLink);

                            // Добавляем уведомление о получении magic link
                            addNotification({
                              type: "success",
                              message:
                                "Ссылка получена! Приложение откроется в браузере.",
                              link: magicLink,
                              userId: user?.id || "",
                            });
                          } catch (error) {
                            console.error(
                              "Ошибка получения magic link:",
                              error
                            );

                            // Добавляем уведомление об ошибке
                            addNotification({
                              type: "error",
                              message:
                                "Ошибка при получении magic link. Попробуйте еще раз.",
                              userId: user?.id || "",
                            });
                          }
                          closeMobileMenu();
                        }}
                      >
                        <Globe size={20} />
                        <span>{t('common.open_in_browser')}</span>
                      </Button>
                    )}

                    <SafeLink
                      to="/legal"
                      className={`nav-link text-base py-3 ${isActivePath("/legal") ? "nav-link-active" : ""
                        }`}
                      onClick={closeMobileMenu}
                    >
                      <Scale size={20} />
                      <span>{t('common.legal')}</span>
                    </SafeLink>
                    {isTelegram ? (
                      <Button
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2"
                        onClick={async () => {
                          const canNavigate = await checkNavigation();
                          if (canNavigate) {
                            closeMobileMenu();
                            try {
                              // Сначала выходим из Supabase
                              await logout();
                              // Затем очищаем данные и закрываем Telegram
                              await logoutAndCloseTelegramApp();
                            } catch (error) {
                              console.error("Ошибка при выходе:", error);
                              // В случае ошибки все равно пытаемся закрыть приложение
                              await logoutAndCloseTelegramApp();
                            }
                          }
                        }}
                      >
                        <ArrowLeft size={20} />
                        <span>Выйти</span>
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2"
                        onClick={async () => {
                          const canNavigate = await checkNavigation();
                          if (canNavigate) {
                            closeMobileMenu();
                            logout();
                          }
                        }}
                      >
                        <LogOut size={20} />
                        <span>{t('common.logout')}</span>
                      </Button>
                    )}
                  </div>
                </nav>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center space-x-2">
            <div className={disabled ? 'pointer-events-none' : ''}>
              <ThemeToggleCompact />
            </div>

            <div className={disabled ? 'pointer-events-none' : ''}>
              <LanguageSwitcher />
            </div>

            <SafeLink to="/login">
              <Button variant="outline" size="sm">
                {t('auth.login.label')}
              </Button>
            </SafeLink>
            <SafeLink to="/register">
              <Button size="sm">{t('auth.register.label')}</Button>
            </SafeLink>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
